import { useAtomValue } from "jotai";
import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
    spectrogramHoverPxAtom,
    spectrogramHoverTimeMsAtom,
    spectrogramIsHoveringAtom,
} from "$/modules/spectrogram/states/index.ts";
import { editingTimeFieldAtom } from "$/states/main.ts";
import { msToTimestamp } from "$/utils/timestamp.ts";
import styles from "./AudioSpectrogram.module.css";
import { RULER_HEIGHT } from "./TimelineRuler.tsx";

interface HoverProps {
    scrollLeft: number;
    isInvalidEndTime: boolean;
    isDragging: boolean;
}

export const HoverOverlays: FC<HoverProps> = ({ isInvalidEndTime, isDragging }) => {
    const { t } = useTranslation();
    const isHovering = useAtomValue(spectrogramIsHoveringAtom);
    const hoverPx = useAtomValue(spectrogramHoverPxAtom);
    const hoverTimeMs = useAtomValue(spectrogramHoverTimeMsAtom);
    const editingTimeField = useAtomValue(editingTimeFieldAtom);

    if (isDragging || !isHovering) return null;

    let hoverTimeFormatted = msToTimestamp(hoverTimeMs);
    let tooltipBgColor: string | undefined;

    if (isInvalidEndTime) {
        hoverTimeFormatted = t("spectrogram.invalidEndTime", "不能选择此结束时间");
        tooltipBgColor = "var(--red-9)";
    } else if (editingTimeField && !editingTimeField.isWord) {
        const fieldName =
            editingTimeField.field === "startTime"
                ? t("ribbonBar.editMode.startTime", "起始时间")
                : t("ribbonBar.editMode.endTime", "结束时间");
        hoverTimeFormatted = `${t("common.clickToSet", "点击设置")}${fieldName}: ${hoverTimeFormatted}`;
        tooltipBgColor = "var(--accent-9)";
    }

    return (
        <>
            <div
                className={styles.hoverTimeTooltip}
                style={{
                    left: `${hoverPx}px`,
                    opacity: 1,
                    backgroundColor: tooltipBgColor,
                }}
            >
                {hoverTimeFormatted}
            </div>

            <div
                className={`${styles.rulerHoverFade} ${styles.rulerHoverFadeLeft}`}
                style={{
                    width: `${hoverPx}px`,
                    height: `${RULER_HEIGHT}px`,
                    opacity: 1,
                }}
            />

            <div
                className={`${styles.rulerHoverFade} ${styles.rulerHoverFadeRight}`}
                style={{
                    left: `${hoverPx}px`,
                    height: `${RULER_HEIGHT}px`,
                    opacity: 1,
                }}
            />
        </>
    );
};

export const HoverCursor: FC<HoverProps> = ({ scrollLeft, isInvalidEndTime, isDragging }) => {
    const isHovering = useAtomValue(spectrogramIsHoveringAtom);
    const hoverPx = useAtomValue(spectrogramHoverPxAtom);
    const editingTimeField = useAtomValue(editingTimeFieldAtom);

    if (isDragging || !isHovering) return null;

    let hoverLineColor: string | undefined;

    if (isInvalidEndTime) {
        hoverLineColor = "var(--red-9)";
    } else if (editingTimeField && !editingTimeField.isWord) {
        hoverLineColor = "var(--accent-9)";
    }

    const hoverX = scrollLeft + hoverPx;

    return (
        <div
            className={styles.hoverCursorContainer}
            style={{
                left: `${hoverX}px`,
                opacity: 1,
            }}
        >
            <div
                className={styles.hoverCursorLine}
                style={{ backgroundColor: hoverLineColor }}
            />
        </div>
    );
};
